/**
 * useBrandEditor — React hook that encapsulates all brand editor state management.
 * Extracted from ProjectBrand.jsx for use in the @imagia/brand-editor SDK.
 * Contributor: ben@hapi.vc
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { generateLogoVariants } from '../utils/brandUtils.js';

export default function useBrandEditor({ client, onStepChange, initialStep }) {
  // ─── Core state ────────────────────────────────────────────
  const [brand, setBrand] = useState(null);
  const [style, setStyle] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStepRaw] = useState(initialStep || 'loading');
  const [working, setWorking] = useState(false);

  function setStep(newStep) {
    setStepRaw(newStep);
    onStepChange?.(newStep);
  }

  // ─── Describe form ─────────────────────────────────────────
  const [brandName, setBrandName] = useState('');
  const [industry, setIndustry] = useState('');
  const [audience, setAudience] = useState('');
  const [description, setDescription] = useState('');

  // ─── Progress stepper ──────────────────────────────────────
  const [progressSteps, setProgressSteps] = useState([]);
  const [progressIndex, setProgressIndex] = useState(0);

  // ─── Style brainstorm ──────────────────────────────────────
  const [proposals, setProposals] = useState([]);
  const [proposalHistory, setProposalHistory] = useState([]);
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [styleFeedback, setStyleFeedback] = useState('');

  // ─── Logo brainstorm ───────────────────────────────────────
  const [logoAssets, setLogoAssets] = useState([]);
  const [selectedLogos, setSelectedLogos] = useState([]);
  const [logoFeedback, setLogoFeedback] = useState('');
  const pollRef = useRef(null);
  const [starredLogos, setStarredLogos] = useState(new Set());
  const [hiddenLogos, setHiddenLogos] = useState(new Set());
  const [showStarredOnly, setShowStarredOnly] = useState(false);
  const [generatingMore, setGeneratingMore] = useState(null);
  const [tweakAsset, setTweakAsset] = useState(null);
  const [tweakEditMode, setTweakEditMode] = useState(false);

  // ─── Import ────────────────────────────────────────────────
  const [importFiles, setImportFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  // ─── Typography editing ────────────────────────────────────
  const [editingTypography, setEditingTypography] = useState(false);
  const [editHeadingFont, setEditHeadingFont] = useState('');
  const [editBodyFont, setEditBodyFont] = useState('');
  const [savingTypography, setSavingTypography] = useState(false);

  // ─── Logo preview ──────────────────────────────────────────
  const [previewSvg, setPreviewSvg] = useState(null);
  const [previewName, setPreviewName] = useState('');

  // ─── Asset editor ──────────────────────────────────────────
  const [editingAsset, setEditingAsset] = useState(null);
  const [brainstormingAssetType, setBrainstormingAssetType] = useState(null);
  const [starredAssets, setStarredAssets] = useState(new Set());
  const [showStarredAssets, setShowStarredAssets] = useState(false);

  // ─── Asset generation ──────────────────────────────────────
  const [activeJobs, setActiveJobs] = useState([]);
  const [generatedAssets, setGeneratedAssets] = useState([]);
  const [generatingAssetType, setGeneratingAssetType] = useState(null);
  const [presentationTopic, setPresentationTopic] = useState('');

  // ─── Load Brand ────────────────────────────────────────────
  const loadBrand = useCallback(async () => {
    try {
      const brandData = await client.getBrand();
      if (brandData.brand) {
        setBrand(brandData.brand);
        setStyle(brandData.style || null);
        setAssets(brandData.assets || []);

        const allAssets = (brandData.assets || []).filter(a => a.status !== 'rejected');
        const logos = allAssets.filter(a => a.asset_type === 'logo');
        if (logos.length > 0) setLogoAssets(logos);
        setGeneratedAssets(allAssets.filter(a => a.asset_type !== 'logo'));

        // Also fetch assets separately
        try {
          const fullAssets = await client.getAssets();
          const all = (fullAssets.assets || fullAssets || []).filter(a => a.status !== 'rejected');
          const fullLogos = all.filter(a => a.asset_type === 'logo');
          if (fullLogos.length > logos.length) setLogoAssets(fullLogos);
          const fullNonLogos = all.filter(a => a.asset_type !== 'logo');
          if (fullNonLogos.length > 0) setGeneratedAssets(fullNonLogos);
        } catch {}

        if (brandData.style) {
          setStep('dashboard');
        } else {
          const bd = typeof brandData.brand.brainstorm_data === 'string'
            ? JSON.parse(brandData.brand.brainstorm_data || '{}')
            : (brandData.brand.brainstorm_data || {});
          if (bd.proposals?.length > 0) {
            setProposals(bd.proposals);
            setStep('styles');
          } else {
            setStep('describe');
            setBrandName(brandData.brand.name || '');
            setIndustry(brandData.brand.industry || '');
            setAudience(brandData.brand.target_audience || '');
            setDescription(brandData.brand.description || '');
          }
        }
      } else {
        setStep('entry');
      }
    } catch {
      setStep('entry');
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => { loadBrand(); }, [loadBrand]);
  useEffect(() => { return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  // Bulk-load proposal fonts
  useEffect(() => {
    if (proposals.length === 0) return;
    const fontSet = new Set();
    proposals.forEach(p => {
      if (p.font_heading) fontSet.add(p.font_heading);
      if (p.font_body) fontSet.add(p.font_body);
    });
    if (fontSet.size === 0) return;
    const families = [...fontSet].map(f => `family=${encodeURIComponent(f)}:wght@400;700`).join('&');
    const href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
    if (document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }, [proposals]);

  // ─── Describe + Generate ───────────────────────────────────
  async function handleDescribe() {
    setWorking(true); setError('');
    const steps = [
      'Creating brand profile...',
      'Analyzing brand personality...',
      'Generating color palettes...',
      'Selecting typography pairings...',
      'Crafting brand voice & tone...',
      'Assembling style proposals...',
    ];
    setProgressSteps(steps);
    setProgressIndex(0);
    setStep('generating');

    try {
      setProgressIndex(1);
      const result = await client.createBrand({ name: brandName, industry, target_audience: audience, description });
      setBrand(result.brand);

      setProgressIndex(2);
      const progressTimer = setInterval(() => {
        setProgressIndex(prev => Math.min(prev + 1, steps.length - 1));
      }, 8000);

      const brainstormResult = await client.brainstorm({
        personality: description,
        mood: description,
        competitors: '',
        keywords: industry,
      }, 5);

      clearInterval(progressTimer);
      setProgressIndex(steps.length);

      if (proposals.length > 0) setProposalHistory(prev => [...prev, proposals]);
      setProposals(brainstormResult.proposals || []);
      setTimeout(() => setStep('styles'), 500);
    } catch (err) {
      setError(err.message);
      setStep('describe');
    } finally { setWorking(false); }
  }

  // ─── Import ────────────────────────────────────────────────
  async function handleImport() {
    if (importFiles.length === 0) return;
    setWorking(true); setError('');
    const steps = [
      'Uploading documents...',
      'Extracting text from files...',
      'Analyzing brand elements with AI...',
      'Creating brand profile...',
      'Applying extracted styles...',
    ];
    setProgressSteps(steps);
    setProgressIndex(0);
    setStep('generating');

    try {
      setProgressIndex(1);
      const formData = new FormData();
      importFiles.forEach(f => formData.append('files', f));

      setProgressIndex(2);
      const result = await client.importBrand(formData);
      setProgressIndex(3);
      setBrand(result.brand);

      setProgressIndex(4);
      const brandData = await client.getBrand();
      setProgressIndex(steps.length);

      if (brandData.style) {
        setStyle(brandData.style);
        setAssets(brandData.assets || []);
        setTimeout(() => setStep('dashboard'), 500);
      } else {
        const brainstormResult = await client.brainstorm({ personality: result.extracted?.description || '' }, 5);
        setProposals(brainstormResult.proposals || []);
        setTimeout(() => setStep('styles'), 500);
      }
    } catch (err) {
      setError(err.message);
      setStep('entry');
    } finally { setWorking(false); }
  }

  // ─── Style Brainstorm ──────────────────────────────────────
  function toggleStyle(index) {
    setSelectedStyles(prev => {
      if (prev.includes(index)) return prev.filter(i => i !== index);
      if (prev.length >= 3) return prev;
      return [...prev, index];
    });
  }

  async function handleRefineStyles() {
    setWorking(true); setError('');
    setProgressSteps(['Analyzing your selections...', 'Incorporating feedback...', 'Generating refined options...']);
    setProgressIndex(0);
    setStep('generating');
    try {
      setProgressIndex(1);
      const result = await client.brainstorm({
        selected_indices: selectedStyles,
        feedback: styleFeedback,
        selected_proposals: selectedStyles.map(i => proposals[i]),
      }, 5);
      setProgressIndex(3);
      setProposalHistory(prev => [...prev, proposals]);
      setProposals(result.proposals || []);
      setSelectedStyles([]);
      setStyleFeedback('');
      setTimeout(() => setStep('styles'), 500);
    } catch (err) {
      setError(err.message);
      setStep('styles');
    } finally { setWorking(false); }
  }

  function handleGoBackStyles() {
    if (proposalHistory.length === 0) return;
    const prev = proposalHistory[proposalHistory.length - 1];
    setProposalHistory(h => h.slice(0, -1));
    setProposals(prev);
    setSelectedStyles([]);
    setStyleFeedback('');
  }

  async function handleSelectStyle(index) {
    setWorking(true); setError('');
    const steps = ['Locking in your style...', 'Applying brand colors & fonts...', 'Generating 10 logo concepts...', 'Rendering logo previews...'];
    setProgressSteps(steps);
    setProgressIndex(0);
    setStep('generating');

    try {
      setProgressIndex(1);
      await client.selectStyle({ proposal_index: index });

      setProgressIndex(2);
      const result = await client.generateAssets(['logo'], { logo: { count: 3 } });

      setProgressIndex(3);
      await new Promise((resolve, reject) => {
        pollRef.current = setInterval(async () => {
          try {
            const job = await client.pollJob(result.job_id);
            const status = job.status || job.job?.status;
            if (status === 'completed') {
              clearInterval(pollRef.current);
              pollRef.current = null;
              resolve();
            } else if (status === 'failed') {
              clearInterval(pollRef.current);
              pollRef.current = null;
              reject(new Error(job.error_message || 'Logo generation failed'));
            }
          } catch {}
        }, 3000);
      });

      setProgressIndex(4);
      const assetsResult = await client.getAssets({ type: 'logo' });
      setLogoAssets(assetsResult.assets || assetsResult || []);
      setTimeout(() => setStep('logos'), 500);
    } catch (err) {
      setError(err.message);
      setStep('logos');
    } finally { setWorking(false); }
  }

  // ─── Logo Brainstorm ───────────────────────────────────────
  function toggleLogo(id) {
    setSelectedLogos(prev => {
      if (prev.includes(id)) return prev.filter(i => i !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  }

  function toggleStarLogo(id) {
    setStarredLogos(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function handleHideLogo(id) {
    setHiddenLogos(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setSelectedLogos(prev => prev.filter(i => i !== id));
  }

  function handleMoreLikeLogo(assetId) {
    const asset = logoAssets.find(a => a.id === assetId);
    if (asset) { setTweakAsset(asset); setTweakEditMode(false); }
  }

  function handleEditLogo(assetId) {
    const asset = logoAssets.find(a => a.id === assetId);
    if (asset) { setTweakAsset(asset); setTweakEditMode(true); }
  }

  async function handleTweakGenerate(prompt, count = 3, directSvg = null) {
    if (!tweakAsset) return;
    const assetId = tweakAsset.id;
    setGeneratingMore(assetId);
    try {
      if (directSvg) {
        const result = await client.refineAsset(assetId, `Use this exact SVG as the new logo (user edited it directly). Only clean up if needed but preserve the design: ${directSvg}`);
        const newAsset = result.asset || result;
        if (newAsset?.id) {
          setLogoAssets(prev => prev.map(a => a.id === assetId ? { ...newAsset } : a));
        }
      } else {
        for (let i = 0; i < count; i++) {
          const result = await client.refineAsset(assetId, `${prompt}${count > 1 ? ` Variant ${i + 1} of ${count}.` : ''} Output ONLY clean SVG code, no explanations or text outside the SVG.`);
          const newAsset = result.asset || result;
          if (newAsset?.id) {
            setLogoAssets(prev => {
              if (prev.some(a => a.id === newAsset.id)) return prev;
              const idx = prev.findIndex(a => a.id === assetId);
              const copy = [...prev];
              copy.splice(idx >= 0 ? idx + 1 + i : 0, 0, newAsset);
              return copy;
            });
          }
        }
      }
      setTweakAsset(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setGeneratingMore(null);
    }
  }

  async function handleDeleteLogo(assetId) {
    setLogoAssets(prev => prev.filter(a => a.id !== assetId));
    setSelectedLogos(prev => prev.filter(i => i !== assetId));
    setStarredLogos(prev => { const n = new Set(prev); n.delete(assetId); return n; });
    try { await client.rejectAsset(assetId); } catch {}
  }

  async function handleRefineLogos() {
    setWorking(true); setError('');
    try {
      const refined = [];
      for (const assetId of selectedLogos) {
        const result = await client.refineAsset(assetId, logoFeedback);
        refined.push(result.asset || result);
      }
      setLogoAssets(prev => {
        const ids = new Set(refined.map(r => r.id));
        return [...refined, ...prev.filter(a => !ids.has(a.id))];
      });
      setSelectedLogos([]);
      setLogoFeedback('');
    } catch (err) {
      setError(err.message);
    } finally { setWorking(false); }
  }

  async function handleSelectLogo(assetId) {
    setWorking(true); setError('');
    setProgressSteps(['Unapproving previous logos...', 'Approving selected logo...', 'Saving brand to project...', 'Finalizing brand identity...']);
    setProgressIndex(0);
    setStep('generating');
    try {
      const currentlyApproved = logoAssets.filter(a => a.status === 'approved' && a.id !== assetId);
      for (const a of currentlyApproved) {
        try { await client._request('PATCH', `/assets/${a.id}`, { status: 'completed' }); } catch {}
      }
      setProgressIndex(1);
      await client.approveAsset(assetId);
      setLogoAssets(prev => prev.map(a => ({ ...a, status: a.id === assetId ? 'approved' : a.status === 'approved' ? 'completed' : a.status })));
      await new Promise(r => setTimeout(r, 500));
      setProgressIndex(2);
      await client.applyBrand();
      setProgressIndex(4);
      await loadBrand();
      setStep('dashboard');
    } catch (err) {
      setError(err.message);
      setStep('logos');
    } finally { setWorking(false); }
  }

  // ─── Dashboard Actions ─────────────────────────────────────
  async function handleSaveTypography() {
    setSavingTypography(true); setError('');
    try {
      await client.updateStyle({ font_heading: editHeadingFont, font_body: editBodyFont });
      await client.applyBrand();
      await loadBrand();
      setEditingTypography(false);
    } catch (err) {
      setError(err.message);
    } finally { setSavingTypography(false); }
  }

  function startJobPolling(jobId, type) {
    const poll = setInterval(async () => {
      try {
        const job = await client.pollJob(jobId);
        const status = job.status || job.job?.status;
        const progress = job.progress || job.job?.progress || 0;
        const currentStep = job.current_step || job.job?.current_step || '';

        setActiveJobs(prev => prev.map(j => j.id === jobId ? { ...j, status, progress, currentStep } : j));

        if (status === 'completed') {
          clearInterval(poll);
          setActiveJobs(prev => prev.filter(j => j.id !== jobId));
          setGeneratingAssetType(null);
          const assetsResult = await client.getAssets();
          setGeneratedAssets((assetsResult.assets || assetsResult || []).filter(a => a.status !== 'rejected' && a.asset_type !== 'logo'));
        } else if (status === 'failed') {
          clearInterval(poll);
          setActiveJobs(prev => prev.filter(j => j.id !== jobId));
          setGeneratingAssetType(null);
          setError(`${type} generation failed`);
        }
      } catch {}
    }, 3000);
    return poll;
  }

  // Resume polling for any saved jobs on mount
  useEffect(() => {
    if (activeJobs.length === 0) return;
    const intervals = activeJobs.filter(j => j.status !== 'completed' && j.status !== 'failed').map(j => startJobPolling(j.id, j.type));
    return () => intervals.forEach(clearInterval);
  }, []); // only on mount

  async function handleGenerateAsset(type, options = {}) {
    setGeneratingAssetType(type);
    setError('');
    try {
      const result = await client.generateAssets([type], options);
      if (result.job_id) {
        const newJob = { id: result.job_id, type, status: 'queued', progress: 0, currentStep: 'Starting...', startedAt: new Date().toISOString() };
        setActiveJobs(prev => [...prev, newJob]);
        startJobPolling(result.job_id, type);
      }
    } catch (err) {
      setError(err.message);
      setGeneratingAssetType(null);
    }
  }

  async function handleRemoveBrand() {
    if (!confirm('Remove brand from this project?')) return;
    setWorking(true);
    try {
      await client.deleteBrand();
      setBrand(null); setStyle(null); setAssets([]);
      setStep('entry');
    } catch (err) { setError(err.message); }
    finally { setWorking(false); }
  }

  async function handleGenerateLogos() {
    setProgressSteps(['Generating logo concepts...', 'Rendering previews...']);
    setProgressIndex(0);
    setStep('generating');
    try {
      const r = await client.generateAssets(['logo'], { logo: { count: 3 } });
      setProgressIndex(1);
      pollRef.current = setInterval(async () => {
        const job = await client.pollJob(r.job_id);
        if ((job.status || job.job?.status) === 'completed') {
          clearInterval(pollRef.current);
          const res = await client.getAssets({ type: 'logo' });
          setLogoAssets(res.assets || res || []);
          setProgressIndex(2);
          setTimeout(() => setStep('logos'), 500);
        }
      }, 3000);
    } catch (err) {
      setError(err.message);
      setStep('logos');
    }
  }

  async function handleChangeStyle() {
    setStep('styles');
    try {
      const r = await client.brainstorm({}, 5);
      setProposals(r.proposals || []);
    } catch (err) {
      setError(err.message);
    }
  }

  // ─── Computed values ───────────────────────────────────────
  function getLogoVariants() {
    const logoAsset = assets.find(a => a.asset_type === 'logo' && a.status === 'approved');
    if (!logoAsset?.content?.startsWith('<svg')) return [];
    const colors = style ? { primary: style.color_primary, background: style.color_background, text: style.color_text } : {};
    return generateLogoVariants(logoAsset.content, colors);
  }

  function getApprovedLogo() {
    return assets.find(a => a.asset_type === 'logo' && a.status === 'approved');
  }

  function getStyleColors() {
    if (!style) return null;
    return {
      primary: style.color_primary,
      secondary: style.color_secondary,
      accent: style.color_accent,
      background: style.color_background,
      surface: style.color_surface,
      text: style.color_text,
      text_muted: style.color_text_muted,
    };
  }

  return {
    // State
    brand, style, assets, loading, error, step, working,
    brandName, industry, audience, description,
    progressSteps, progressIndex,
    proposals, proposalHistory, selectedStyles, styleFeedback,
    logoAssets, selectedLogos, logoFeedback,
    starredLogos, hiddenLogos, showStarredOnly, generatingMore,
    tweakAsset, tweakEditMode,
    importFiles, dragOver,
    editingTypography, editHeadingFont, editBodyFont, savingTypography,
    previewSvg, previewName,
    editingAsset, brainstormingAssetType,
    starredAssets, showStarredAssets,
    activeJobs, generatedAssets, generatingAssetType, presentationTopic,

    // Setters
    setError, setStep,
    setBrandName, setIndustry, setAudience, setDescription,
    setSelectedStyles, setStyleFeedback,
    setSelectedLogos, setLogoFeedback,
    setShowStarredOnly, setHiddenLogos,
    setTweakAsset, setTweakEditMode,
    setImportFiles, setDragOver,
    setEditingTypography, setEditHeadingFont, setEditBodyFont,
    setPreviewSvg, setPreviewName,
    setEditingAsset, setBrainstormingAssetType,
    setStarredAssets, setShowStarredAssets,
    setPresentationTopic,

    // Actions
    loadBrand,
    handleDescribe,
    handleImport,
    toggleStyle,
    handleRefineStyles,
    handleGoBackStyles,
    handleSelectStyle,
    toggleLogo,
    toggleStarLogo,
    handleHideLogo,
    handleMoreLikeLogo,
    handleEditLogo,
    handleTweakGenerate,
    handleDeleteLogo,
    handleRefineLogos,
    handleSelectLogo,
    handleSaveTypography,
    handleGenerateAsset,
    handleRemoveBrand,
    handleGenerateLogos,
    handleChangeStyle,

    // Computed
    getLogoVariants,
    getApprovedLogo,
    getStyleColors,
  };
}
